import type { CSSVariablesResolver, MantineThemeOverride, MantineColorsTuple } from "@mantine/core";
import type { ThemeId } from "./prefs";

export interface AppThemeDef {
  id: ThemeId;
  scheme: "light" | "dark";
  swatch: { bg: string; surface: string; accent: string };
  mantine: MantineThemeOverride;
  vars: CSSVariablesResolver;
}

export const DEFAULT_THEME: ThemeId = "clean";

const cleanAccent: MantineColorsTuple = [
  "#eef2ff",
  "#e0e7ff",
  "#c7d2fe",
  "#a5b4fc",
  "#818cf8",
  "#6366f1",
  "#4f46e5",
  "#4338ca",
  "#3730a3",
  "#312e81",
];

const clean: AppThemeDef = {
  id: "clean",
  scheme: "light",
  swatch: { bg: "#f6f7fb", surface: "#ffffff", accent: "#4f46e5" },
  mantine: {
    primaryColor: "app-accent",
    primaryShade: 6,
    defaultRadius: "md",
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    colors: { "app-accent": cleanAccent },
    shadows: {
      xs: "0 1px 2px rgba(0,0,0,.05), 0 4px 14px rgba(30,40,90,.06)",
      sm: "0 1px 2px rgba(0,0,0,.05), 0 4px 14px rgba(30,40,90,.06)",
    },
  },
  vars: () => ({
    variables: {},
    light: {
      "--app-surface": "#ffffff",
      "--mantine-color-body": "#f6f7fb",
      "--mantine-color-default-border": "#eceef5",
    },
    dark: {},
  }),
};

const SANS = "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif";
const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

interface ThemeSpec {
  id: ThemeId;
  scheme: "light" | "dark";
  body: string;
  surface: string;
  border: string;
  text: string;
  dimmed: string;
  /** Visible accent shown in the picker swatch (may be more vivid than the AA-safe filled shade). */
  swatchAccent: string;
  /** Light: shade 6 is filled/text. Dark: shade 8 is filled, shades 3-4 are text on dark. */
  accent: MantineColorsTuple;
  /** Dark schemes only: 0 text, 2 dimmed, 3 placeholder, 4 border, 5 hover, 6 surface/inputs, 7 body. */
  dark?: MantineColorsTuple;
  radius: MantineThemeOverride["defaultRadius"];
  extra?: MantineThemeOverride;
}

/**
 * Builds a theme from its tokens. Named Mantine palettes (green, red, …) are never touched,
 * so status colors stay identical across themes; only `app-accent` (and `dark` on dark schemes) vary.
 */
function makeTheme(s: ThemeSpec): AppThemeDef {
  const colors: MantineThemeOverride["colors"] = { "app-accent": s.accent };
  if (s.dark) colors.dark = s.dark;
  const schemeVars: Record<string, string> = {
    "--mantine-color-body": s.body,
    "--mantine-color-text": s.text,
    "--mantine-color-dimmed": s.dimmed,
    "--mantine-color-default-border": s.border,
  };
  if (s.scheme === "light") schemeVars["--mantine-color-placeholder"] = s.dimmed;
  return {
    id: s.id,
    scheme: s.scheme,
    swatch: { bg: s.body, surface: s.surface, accent: s.swatchAccent },
    mantine: {
      primaryColor: "app-accent",
      // Mantine defaults (light 6 / dark 8): changing the active shade would shift every status palette.
      // Dark schemes also set `light: 8` because autoContrast resolves palette names (e.g. "app-accent",
      // "green") with the *light* primary shade; matching it to the rendered dark shade keeps the
      // black/white text choice computed on the colour actually painted. The light CSS block is inactive
      // under a forced dark scheme, so this has no other effect.
      primaryShade: s.scheme === "dark" ? { light: 8, dark: 8 } : { light: 6, dark: 8 },
      defaultRadius: s.radius,
      fontFamily: SANS,
      colors,
      // Dark schemes: pick black/white text on filled colors by luminance so that filled
      // status badges (green-8, teal-8, orange-8, yellow-8, cyan-8) and bright accents reach AA.
      ...(s.scheme === "dark" ? { autoContrast: true, luminanceThreshold: 0.19 } : {}),
      ...s.extra,
    },
    vars: () => ({
      variables: { "--app-surface": s.surface, "--app-border": s.border },
      light: s.scheme === "light" ? schemeVars : {},
      dark: s.scheme === "dark" ? schemeVars : {},
    }),
  };
}

const aurora = makeTheme({
  id: "aurora",
  scheme: "dark",
  body: "#0b1020",
  surface: "rgba(255,255,255,.07)",
  border: "rgba(255,255,255,.12)",
  text: "#e8ecff",
  dimmed: "#a3abd0",
  swatchAccent: "#6d5cff",
  accent: ["#f3f2ff", "#e3dfff", "#d2ccff", "#c1baff", "#b0a7ff", "#9f94ff", "#8f81ff", "#7e6fff", "#6d5cff", "#5c4dd6"],
  dark: ["#e8ecff", "#c9cfee", "#a3abd0", "#8f98bc", "#2f3654", "#252b47", "#1a2038", "#0b1020", "#080c18", "#05070f"],
  radius: "md",
});

const industrial = makeTheme({
  id: "industrial",
  scheme: "dark",
  body: "#17181b",
  surface: "#202126",
  border: "#2c2d33",
  text: "#d9d9d6",
  dimmed: "#a2a29d",
  swatchAccent: "#ffb020",
  accent: ["#fff9ed", "#fff0d4", "#ffe7ba", "#ffdda0", "#ffd487", "#ffcb6d", "#ffc253", "#ffb93a", "#ffb020", "#d6941b"],
  dark: ["#d9d9d6", "#bdbdb9", "#a2a29d", "#959598", "#2c2d33", "#28292f", "#202126", "#17181b", "#121315", "#0c0d0e"],
  radius: "xs",
  extra: { fontFamilyMonospace: MONO, headings: { fontFamily: MONO } },
});

const playful = makeTheme({
  id: "playful",
  scheme: "light",
  body: "#fff8ef",
  surface: "#ffffff",
  border: "#2b2140",
  text: "#2b2140",
  dimmed: "#665a78",
  swatchAccent: "#ff5c8a",
  // #ff5c8a is only 2.9:1 against white; shade 6 is darkened (#b8285b) to pass AA as fill, text and light-variant text.
  accent: ["#fff0f5", "#ffd6e3", "#ffb3cb", "#ff8fb0", "#ff5c8a", "#e8457a", "#b8285b", "#9e2250", "#8e1e47", "#6f1737"],
  radius: "lg",
  extra: {
    shadows: {
      xs: "2px 2px 0 #2b2140",
      sm: "3px 3px 0 #2b2140",
      md: "3px 3px 0 #2b2140",
      lg: "5px 5px 0 #2b2140",
      xl: "6px 6px 0 #2b2140",
    },
  },
});

const cleanDark = makeTheme({
  id: "clean-dark",
  scheme: "dark",
  body: "#0f1117",
  surface: "#171a23",
  border: "#262a36",
  text: "#e6e8ef",
  dimmed: "#9ca2b5",
  swatchAccent: "#8b85ff",
  // #8b85ff sits at shade 4 (text/outline on dark); shade 8 (filled) is deeper so white text passes AA.
  accent: ["#f2f1ff", "#e1dfff", "#cfccff", "#bdb8ff", "#8b85ff", "#7c75f5", "#6d66ec", "#635ce6", "#5b54e0", "#4c47bc"],
  dark: ["#e6e8ef", "#c5c9d6", "#9ca2b5", "#868ca1", "#262a36", "#1f2330", "#171a23", "#0f1117", "#0b0c11", "#07080b"],
  radius: "md",
  extra: { shadows: clean.mantine.shadows },
});

const ocean = makeTheme({
  id: "ocean",
  scheme: "light",
  body: "#effafb",
  surface: "#ffffff",
  border: "#d3eef2",
  text: "#0c2b33",
  dimmed: "#466870",
  swatchAccent: "#0891b2",
  // #0891b2 is 3.7:1 on white; shade 6 uses #0c6a84 so light-variant text on the body also clears 4.5:1.
  accent: ["#ecfeff", "#cffafe", "#a5f3fc", "#67e8f9", "#22d3ee", "#0891b2", "#0c6a84", "#0a5a70", "#0a4f62", "#073c4b"],
  radius: "md",
  extra: { radius: { md: "14px" } },
});

const sunset = makeTheme({
  id: "sunset",
  scheme: "light",
  body: "#fff7f2",
  surface: "#ffffff",
  border: "#fde2d3",
  text: "#3a1d14",
  dimmed: "#7a4f40",
  swatchAccent: "#f0543c",
  // #f0543c is 3.5:1 on white; shade 6 uses #b3301c so light-variant text on the body also clears 4.5:1.
  accent: ["#fff1ec", "#ffdcd1", "#ffbfae", "#ff9a62", "#f77a5c", "#f0543c", "#b3301c", "#962817", "#882315", "#681b10"],
  radius: "md",
  extra: { radius: { md: "14px" } },
});

const forest = makeTheme({
  id: "forest",
  scheme: "light",
  body: "#f3f5ef",
  surface: "#fbfcf8",
  border: "#dde3d3",
  text: "#1f2a1d",
  dimmed: "#5a6656",
  swatchAccent: "#4d7c4a",
  // Shade 6 nudged from #4d7c4a to #436c41 so text and light-variant text clear 4.5:1 on body and surface.
  accent: ["#f1f5f0", "#dce7da", "#bfd3bc", "#9fbc9b", "#7fa47b", "#5f8c5b", "#436c41", "#3a5e38", "#325031", "#273f26"],
  radius: "sm",
});

const neon = makeTheme({
  id: "neon",
  scheme: "dark",
  body: "#07060d",
  surface: "#110f1d",
  border: "#2c2350",
  text: "#f1e9ff",
  dimmed: "#ab9dcc",
  swatchAccent: "#ff2fd1",
  // #ff2fd1 sits at shade 4 (text/outline/glow); shade 8 (filled) is deeper so white text passes AA.
  accent: ["#ffeefb", "#ffd3f4", "#ffb0ec", "#ff7fe1", "#ff2fd1", "#ec22c0", "#d91cb0", "#cc19a5", "#c0179a", "#a11381"],
  dark: ["#f1e9ff", "#d6c9f0", "#ab9dcc", "#9283b8", "#2c2350", "#1b1730", "#110f1d", "#07060d", "#040308", "#020104"],
  radius: "xs",
});

const paper = makeTheme({
  id: "paper",
  scheme: "light",
  body: "#fbfaf7",
  surface: "#fbfaf7",
  border: "#141414",
  text: "#141414",
  dimmed: "#595959",
  swatchAccent: "#141414",
  accent: ["#f2f2f2", "#d9d9d9", "#bfbfbf", "#a6a6a6", "#808080", "#595959", "#141414", "#2b2b2b", "#0e0e0e", "#000000"],
  radius: 0,
  extra: {
    headings: { fontFamily: 'Georgia, "Times New Roman", serif' },
    radius: { xs: "0px", sm: "0px", md: "0px", lg: "0px", xl: "0px" },
    shadows: { xs: "none", sm: "none", md: "none", lg: "none", xl: "none" },
  },
});

export const APP_THEMES: Record<ThemeId, AppThemeDef> = {
  clean,
  aurora,
  industrial,
  playful,
  "clean-dark": cleanDark,
  ocean,
  sunset,
  forest,
  neon,
  paper,
};
